import { useStore } from '@/store';
import { useEffect } from 'react';
import { getEntries } from '@morph-mapper/utils';
import { DirtyType } from '@/types';
import { useDirtyEntry } from '../api/templating/query';
import { useRenderTemplate } from './use-render-template';
import { iteratorKeys } from '@/config';
import Queue from 'queue-fifo';

export const useGlobalEntryListener = () => {
  const [
    dirtyTemplates,
    dirtyUnderlyings,
    removeDirtyBatch,
    setComputed,
    setTemplateValue,
    getKey,
    addDirty,
    addDirtyAll,
    getRequiredBy,
  ] = useStore(({ entries: e }) => [
    e.getAllDirty(DirtyType.Template),
    e.getAllDirty(DirtyType.Underlying),
    e.removeDirtyBatch,
    e.setComputed,
    e.setTemplateValue,
    e.getKey,
    e.addDirty,
    e.addDirtyAll,
    e.getRequiredBy,
  ]);
  const { data, error } = useDirtyEntry(dirtyTemplates);
  const { renderItemEntry } = useRenderTemplate();

  useEffect(() => {
    if (dirtyUnderlyings.length === 0) return;

    const isIteratorEntry = (entryId: string) =>
      iteratorKeys.includes(getKey(entryId));
    const orderedDirtyUnderlyings = [...dirtyUnderlyings].sort((a, b) => {
      const aIsIterator = isIteratorEntry(a) ? 1 : 0;
      const bIsIterator = isIteratorEntry(b) ? 1 : 0;
      return bIsIterator - aIsIterator;
    });

    orderedDirtyUnderlyings.forEach((entryId) => {
      const templateValue = renderItemEntry(entryId);
      setTemplateValue(entryId, templateValue);
    });
    removeDirtyBatch(orderedDirtyUnderlyings, DirtyType.Underlying);

    // update both the field and dependents when not computed
    const ids = new Set(orderedDirtyUnderlyings);
    const depends = new Set<string>();
    const queue = new Queue<string>();
    orderedDirtyUnderlyings.forEach((entryId) => queue.enqueue(entryId));

    while (!queue.isEmpty()) {
      const entryId = queue.dequeue()!;

      getRequiredBy(entryId).forEach((dependentId) => {
        if (ids.has(dependentId) || depends.has(dependentId)) {
          return;
        }

        depends.add(dependentId);
        queue.enqueue(dependentId);
      });
    }

    depends.forEach((dependentId) => {
      addDirty(dependentId, DirtyType.Template);
    });

    const hasIteratorInBatch = orderedDirtyUnderlyings.some(isIteratorEntry);
    if (hasIteratorInBatch) {
      addDirtyAll(DirtyType.Template);
    }
  }, [
    addDirty,
    addDirtyAll,
    dirtyUnderlyings,
    getKey,
    getRequiredBy,
    removeDirtyBatch,
    renderItemEntry,
    setTemplateValue,
  ]);

  useEffect(() => {
    if (error === null || dirtyTemplates.length === 0) return;

    dirtyTemplates.forEach((entryId) => {
      setComputed(entryId, null);
    });
    removeDirtyBatch(dirtyTemplates, DirtyType.Template);
  }, [dirtyTemplates, error, removeDirtyBatch, setComputed]);

  useEffect(() => {
    if (dirtyTemplates.length === 0) return;
    if (data === undefined) return;

    const entries = getEntries(data);
    entries.forEach(([key, result]) => {
      setComputed(key, result);
    });

    removeDirtyBatch(dirtyTemplates, DirtyType.Template);
  }, [data, dirtyTemplates, removeDirtyBatch, setComputed]);
};
